import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';
import { sectionRenderer } from '@theme/section-renderer';

const LAZY_SLIDES_SECTION_ID = 'section-rendering-cart-upsell';
const LAZY_PREFETCH_TRIGGER = '.nw-product-card__actions-desktop, .nw-product-card__actions-mobile';
// Add buttons of the product page (main form, sticky bar, complete-look cards) for the modal with deferred slides.
const PDP_PREFETCH_TRIGGER = 'add-to-cart-component, sticky-add-to-cart';

class NwCartUpsellModal extends HTMLElement {
  #abortController;
  #renderedSlidesUrl = null;
  #loadingSlidesUrl = null;
  #slidesRequestToken = null;
  /** @type {{ form: HTMLElement, productId: string } | null} Addition shown before the cart answered, until it is confirmed or fails. */
  #pendingAdd = null;
  /** @type {number | null} Cart total (minor units) of the last cart update. */
  #lastCartTotal = null;
  /** @type {{ title: string, url: string, image: { src: string | null, srcset: string | null, alt: string } | null } | null} */
  #productSnapshot = null;

  connectedCallback() {
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { capture: true, signal });
    document.addEventListener(ThemeEvents.cartAddStart, this.#handleAddStart, { capture: true, signal });
    document.addEventListener(ThemeEvents.cartAddFailed, this.#handleAddFailed, { capture: true, signal });

    // The product page modal shows its own product when it opens ahead of the cart's answer, so keep what Liquid
    // rendered: an accessory added from "complete the look" replaces it in the meantime.
    if (!this.#isLazy && !this.#productSnapshot) this.#snapshotProduct();

    if (this.#isLazy) {
      // Prefetch the slides as soon as the shopper interacts with a card's add-to-cart area,
      // so the modal usually opens with its recommendations already in place.
      const root = this.closest('.shopify-section') || document;
      root.addEventListener('pointerover', this.#prefetchSlides, { passive: true, signal });
      root.addEventListener('pointerdown', this.#prefetchSlides, { passive: true, signal });
      root.addEventListener('focusin', this.#prefetchSlides, { signal });
    } else if (this.#deferredSlidesUrl) {
      // The product page modal fetches its recommendations when the shopper heads for an add button.
      document.addEventListener('pointerover', this.#prefetchDeferredSlides, { passive: true, signal });
      document.addEventListener('pointerdown', this.#prefetchDeferredSlides, { passive: true, signal });
      document.addEventListener('focusin', this.#prefetchDeferredSlides, { signal });
    }
  }

  /** Product URL of the modal whose slides are fetched on demand (product page), if any. */
  get #deferredSlidesUrl() {
    return this.hasAttribute('data-lazy') ? null : this.dataset.slidesUrl || null;
  }

  /** @param {Event} event */
  #prefetchDeferredSlides = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const url = this.#deferredSlidesUrl;
    if (!url || !target?.closest(PDP_PREFETCH_TRIGGER) || this.contains(target)) return;

    this.#fetchSlides(url).catch(() => {});
  };

  get #isLazy() {
    return this.hasAttribute('data-lazy');
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /**
   * Works out whether an addition is one this modal shows, from where it started.
   * @param {EventTarget | null} target - The product form that sent the addition
   * @param {string | number | undefined} productId
   * @param {string | undefined} source
   */
  #classifyAdd(target, productId, source) {
    const eventStartedInsideVariantDialog = target instanceof Element && target.closest('quick-add-dialog') !== null;
    // A recommendation with sizes opens the size selector on top of this modal: that addition belongs to the modal
    // (unlike the one the modal opened for, still pending its confirmation).
    const eventStartedInsideModal =
      (target instanceof Node && this.contains(target)) ||
      (eventStartedInsideVariantDialog &&
        this.querySelector('dialog')?.open === true &&
        !this.#isFromPendingAdd(target));
    // "Complete the look" cards add products with sizes through the shared size selector: an addition from there of a
    // product shown in that block counts as one of its additions.
    const eventStartedInsideCompleteLook =
      target instanceof Element &&
      (target.closest('.nw-complete-look-mini') !== null ||
        (eventStartedInsideVariantDialog && this.#isCompleteLookProduct(productId)));
    // An addition from the size selector (quick-add dialog) of the page's product, or of a grid card, counts as that
    // product's addition: the modal opens on top of the selector, which closes itself once the cart confirms.
    const lazyUpsellCard = this.#isLazy
      ? this.#getLazyUpsellCard(target, productId) ||
        (eventStartedInsideVariantDialog ? this.#findLazyUpsellCard(productId) : null)
      : null;
    const isCurrentPdpAdd =
      source === 'product-form-component' &&
      (this.#isLazy ? lazyUpsellCard !== null : String(productId || '') === String(this.dataset.productId || '')) &&
      !eventStartedInsideModal;
    const isCompleteLookAdd = eventStartedInsideCompleteLook && !eventStartedInsideModal;

    return {
      eventStartedInsideModal,
      eventStartedInsideVariantDialog,
      lazyUpsellCard,
      isCurrentPdpAdd,
      isCompleteLookAdd,
      isLazyCardAdd: isCurrentPdpAdd && lazyUpsellCard !== null,
    };
  }

  /**
   * Opens the modal as soon as the product page (or a grid card) sends its addition, with what the page already
   * knows; the cart's answer then fills in the rest. "Complete the look" additions keep waiting for the answer.
   * @param {CustomEvent} event
   */
  #handleAddStart = (event) => {
    const detail = event.detail || {};
    const form = event.target;
    const { isCurrentPdpAdd, isCompleteLookAdd, lazyUpsellCard, isLazyCardAdd } = this.#classifyAdd(
      form,
      detail.productId,
      detail.source
    );

    if (!isCurrentPdpAdd || isCompleteLookAdd || !(form instanceof HTMLElement)) return;

    this.#pendingAdd = { form, productId: String(detail.productId || '') };
    this.#showProvisionalProduct(form, detail, lazyUpsellCard);

    if (isLazyCardAdd) {
      this.#renderLazySlides(lazyUpsellCard.dataset.cartUpsellUrl, String(detail.productId));
    } else if (this.#deferredSlidesUrl) {
      this.#renderLazySlides(this.#deferredSlidesUrl, String(this.dataset.productId));
    }

    this.querySelector('dialog-component')?.showDialog();
  };

  /**
   * Whether an event comes from the pending addition's form. Also matched by product, in case the form was
   * re-rendered meanwhile (a size change re-renders the product section).
   * @param {EventTarget | null} target
   */
  #isFromPendingAdd(target) {
    const pending = this.#pendingAdd;
    if (!pending || !(target instanceof Element)) return false;
    if (pending.form.contains(target)) return true;

    const form = target.closest('product-form-component');
    return form instanceof HTMLElement && pending.productId !== '' && form.dataset.productId === pending.productId;
  }

  /** @param {Event} event */
  #handleAddFailed = (event) => {
    if (!this.#isFromPendingAdd(event.target)) return;

    // The form shows why it failed (no stock, maximum per order…), so the modal gets out of the way. Closed on the next
    // frame: the dialog opens in an animation frame too, and a very fast failure could otherwise arrive before it.
    this.#pendingAdd = null;
    this.#clearPendingState();
    requestAnimationFrame(() => this.querySelector('dialog-component')?.closeDialog?.());
  };

  #handleCartUpdate = (event) => {
    const detail = event.detail;
    const data = detail?.data;

    if (detail?.resource?.items) {
      const total = Number(detail.resource.total_price);
      if (Number.isFinite(total)) this.#lastCartTotal = total;
    }

    if (!data || data.didError) return;

    const pendingAdd = this.#isFromPendingAdd(event.target) ? this.#pendingAdd : null;
    const {
      eventStartedInsideModal,
      eventStartedInsideVariantDialog,
      lazyUpsellCard,
      isCurrentPdpAdd,
      isCompleteLookAdd,
      isLazyCardAdd,
    } = this.#classifyAdd(event.target, data.productId, data.source);

    // Keeps the previous behaviour of the per-card modals: variant dialog adds only suppressed the cart drawer
    // when at least one card on the page had recommendations.
    const suppressVariantDialogAdd = eventStartedInsideVariantDialog && (!this.#isLazy || this.#hasLazyUpsellCards());

    if (eventStartedInsideModal || suppressVariantDialogAdd || isCurrentPdpAdd || isCompleteLookAdd) {
      data.suppressCartDrawerAutoOpen = true;
    }

    const cart = detail.resource;
    const dialog = this.querySelector('dialog');

    if (dialog?.open && cart?.items) {
      this.#updateFreeShipping(cart);
    }

    if (!isCurrentPdpAdd && !isCompleteLookAdd) return;

    if (pendingAdd) {
      this.#pendingAdd = null;
      this.#clearPendingState();
    }

    const sourceProductImage =
      isCompleteLookAdd && event.target instanceof Element
        ? event.target.querySelector('add-to-cart-component')?.dataset.productVariantMedia
        : null;

    if (isCompleteLookAdd) {
      const sourceDialog = event.target instanceof Element ? event.target.closest('dialog-component') : null;
      if (sourceDialog && !this.contains(sourceDialog)) {
        sourceDialog.closeDialog?.();
      }
    }

    if (isLazyCardAdd) {
      // The lazy shell has no server-rendered product info, so fall back to the cart/add.js line item.
      this.#updateAddedProduct(cart, data, data.addedItem || null);
    } else if (cart?.items || (isCompleteLookAdd && data.addedItem)) {
      this.#updateAddedProduct(cart, data, isCompleteLookAdd ? data.addedItem : null, sourceProductImage);
    }

    if (cart?.items) {
      this.#updateFreeShipping(cart);
    }

    if (isLazyCardAdd) {
      this.#renderLazySlides(lazyUpsellCard.dataset.cartUpsellUrl, String(data.productId));
    } else if (this.#deferredSlidesUrl) {
      this.#renderLazySlides(this.#deferredSlidesUrl, String(this.dataset.productId));
    }

    // Already opened when the addition was sent (and left closed if the shopper dismissed it meanwhile).
    if (!pendingAdd) this.querySelector('dialog-component')?.showDialog();
  };

  /**
   * Fills the summary before the cart answers: product, size and quantity from the page, and the price and free
   * shipping progress when the size's price is known. Whatever is still unknown stays hidden until the answer.
   * @param {HTMLElement} form - The product form that sent the addition
   * @param {{ variantId?: string, quantity?: number }} detail
   * @param {HTMLElement | null} lazyUpsellCard - The grid card, for the shared modal of collections and search
   */
  #showProvisionalProduct(form, detail, lazyUpsellCard) {
    const variantId = String(detail.variantId || '');
    const quantity = Number(detail.quantity) || 1;
    // The size was picked in the size selector when the addition comes from there.
    const scope = form.closest('quick-add-dialog') || lazyUpsellCard || form.closest('.shopify-section') || document;
    const option = variantId ? scope.querySelector(`[data-variant-id="${CSS.escape(variantId)}"]`) : null;
    const unitPrice = option instanceof HTMLElement && option.dataset.variantPrice ? Number(option.dataset.variantPrice) : NaN;
    const variantTitle =
      option instanceof HTMLInputElement || option instanceof HTMLOptionElement ? option.value.trim() : '';

    if (lazyUpsellCard) {
      const image =
        lazyUpsellCard.querySelector('[class*="nw-product-card__media-frame"] img') || lazyUpsellCard.querySelector('img');
      const title = lazyUpsellCard.querySelector('.nw-product-card__title')?.textContent?.trim() || '';
      this.#setAddedProductDetails(
        title,
        lazyUpsellCard.dataset.cartUpsellUrl || '',
        image instanceof HTMLImageElement ? image.currentSrc || image.src : ''
      );
    } else {
      this.#restoreProductSnapshot();
    }

    const variant = this.querySelector('[data-added-product-variant]');
    if (variant) {
      variant.textContent = variantTitle;
      variant.toggleAttribute('hidden', !variantTitle);
    }

    const quantityElement = this.querySelector('[data-added-product-quantity]');
    if (quantityElement) quantityElement.textContent = String(quantity);

    const price = this.querySelector('[data-added-product-price]');
    const cartTotal = this.#currentCartTotal();
    const priceKnown = Number.isFinite(unitPrice);

    if (priceKnown && price) price.textContent = this.#formatMoney(unitPrice * quantity);
    this.toggleAttribute('data-price-pending', !priceKnown);

    if (priceKnown && cartTotal !== null) {
      this.#updateFreeShipping({ total_price: cartTotal + unitPrice * quantity });
    }
    this.toggleAttribute('data-shipping-pending', !priceKnown || cartTotal === null);
  }

  #clearPendingState() {
    this.removeAttribute('data-price-pending');
    this.removeAttribute('data-shipping-pending');
  }

  /** @returns {number | null} The cart total before this addition, in minor units. */
  #currentCartTotal() {
    if (this.#lastCartTotal !== null) return this.#lastCartTotal;

    const renderedTotal = this.querySelector('[data-cart-free-shipping]')?.getAttribute('data-cart-total');
    if (!renderedTotal) return null;

    const total = Number(renderedTotal);
    return Number.isFinite(total) ? total : null;
  }

  #snapshotProduct() {
    const image = this.querySelector('.nw-cart-upsell-modal__image');

    this.#productSnapshot = {
      title: this.querySelector('[data-added-product-title]')?.textContent || '',
      url: this.querySelector('[data-added-product-link]')?.getAttribute('href') || '',
      image:
        image instanceof HTMLImageElement
          ? { src: image.getAttribute('src'), srcset: image.getAttribute('srcset'), alt: image.alt }
          : null,
    };
  }

  #restoreProductSnapshot() {
    const snapshot = this.#productSnapshot;
    if (!snapshot) return;

    const title = this.querySelector('[data-added-product-title]');
    if (title) title.textContent = snapshot.title;
    this.querySelectorAll('[data-added-product-link]').forEach((link) => {
      if (link instanceof HTMLAnchorElement && snapshot.url) link.href = snapshot.url;
    });

    const image = this.querySelector('.nw-cart-upsell-modal__image');
    if (image instanceof HTMLImageElement && snapshot.image) {
      if (snapshot.image.srcset) image.setAttribute('srcset', snapshot.image.srcset);
      if (snapshot.image.src) image.src = snapshot.image.src;
      image.alt = snapshot.image.alt;
      image.hidden = false;
    }
  }

  /**
   * @param {string} title
   * @param {string} url
   * @param {string} imageSrc
   */
  #setAddedProductDetails(title, url, imageSrc) {
    if (title.includes('""')) {
      if (title.startsWith('"') && title.endsWith('"')) title = title.slice(1, -1);
      title = title.replaceAll('""', '"');
    }
    const titleElement = this.querySelector('[data-added-product-title]');
    const image = this.querySelector('.nw-cart-upsell-modal__image');

    if (titleElement) titleElement.textContent = title;
    if (url) {
      this.querySelectorAll('[data-added-product-link]').forEach((link) => {
        if (link instanceof HTMLAnchorElement) link.href = url;
      });
    }

    if (image instanceof HTMLImageElement) {
      if (imageSrc) {
        // Remove the Liquid srcset first, otherwise the browser can keep showing the product page image.
        image.removeAttribute('srcset');
        image.src = imageSrc;
        image.alt = title;
        image.hidden = false;
      } else {
        image.hidden = true;
      }
    }
  }

  /**
   * Returns the grid item of the added product when it has lazy recommendations.
   * @param {EventTarget | null} target
   * @param {string | number | undefined} productId
   * @returns {HTMLElement | null}
   */
  #getLazyUpsellCard(target, productId) {
    if (!(target instanceof Element) || !productId) return null;

    const card = target.closest('[data-cart-upsell-url]');
    if (!(card instanceof HTMLElement) || String(card.dataset.productId) !== String(productId)) return null;

    const section = this.closest('.shopify-section');
    return !section || section.contains(card) ? card : null;
  }

  /**
   * Returns the grid item of a product with lazy recommendations, for additions made outside the card.
   * @param {string | number | undefined} productId
   * @returns {HTMLElement | null}
   */
  #findLazyUpsellCard(productId) {
    if (!productId) return null;

    const root = this.closest('.shopify-section') || document;
    const card = root.querySelector(`[data-cart-upsell-url][data-product-id="${CSS.escape(String(productId))}"]`);
    return card instanceof HTMLElement ? card : null;
  }

  /** @param {string | number | undefined} productId */
  #isCompleteLookProduct(productId) {
    if (this.#isLazy || !productId) return false;

    return document.querySelector(`.nw-complete-look-mini [data-product-id="${CSS.escape(String(productId))}"]`) !== null;
  }

  #hasLazyUpsellCards() {
    const root = this.closest('.shopify-section') || document;
    return root.querySelector('[data-cart-upsell-url]') !== null;
  }

  /** @param {Event} event */
  #prefetchSlides = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target?.closest(LAZY_PREFETCH_TRIGGER) || this.contains(target)) return;

    const card = target.closest('[data-cart-upsell-url]');
    if (card instanceof HTMLElement && card.dataset.cartUpsellUrl) {
      this.#fetchSlides(card.dataset.cartUpsellUrl).catch(() => {});
    }
  };

  /**
   * Fetches the recommendation slides of a product. Requests are cached and de-duplicated by the section renderer.
   * @param {string} productUrl
   * @returns {Promise<string>}
   */
  async #fetchSlides(productUrl) {
    const html = await sectionRenderer.getSectionHTML(
      LAZY_SLIDES_SECTION_ID,
      true,
      new URL(productUrl, window.location.origin)
    );
    const slides = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-upsell-slides]');

    return slides?.innerHTML.trim() || '';
  }

  /**
   * Shows the skeleton, then injects the product recommendations into the shared carousel.
   * @param {string | undefined} productUrl
   * @param {string} productId
   */
  async #renderLazySlides(productUrl, productId) {
    const carousel = this.querySelector('[data-cart-upsell-carousel]');
    const wrapper = carousel?.querySelector('.swiper-wrapper');
    const skeleton = this.querySelector('[data-cart-upsell-skeleton]');

    if (!(carousel instanceof HTMLElement) || !wrapper || !productUrl) return;
    if (this.#renderedSlidesUrl === productUrl && !carousel.hidden) return;
    // Already on its way since the modal opened, ahead of the cart's answer.
    if (this.#loadingSlidesUrl === productUrl) return;

    const requestToken = Symbol(productUrl);
    this.#slidesRequestToken = requestToken;
    this.#loadingSlidesUrl = productUrl;
    this.#renderedSlidesUrl = null;
    carousel.hidden = true;
    if (skeleton instanceof HTMLElement) skeleton.hidden = false;

    let slidesHTML = '';
    try {
      slidesHTML = await this.#fetchSlides(productUrl);
    } catch (error) {
      console.error(error);
    }

    if (this.#slidesRequestToken !== requestToken) return;
    this.#loadingSlidesUrl = null;
    if (skeleton instanceof HTMLElement) skeleton.hidden = true;

    // Without recommendations the modal still confirms the add, just without the carousel.
    if (!slidesHTML) return;

    // Same list id the per-card modals used, so analytics keeps one list per product.
    carousel.dataset.analyticsListId = `nw-cart-upsell-${this.dataset.sectionId}-${productId}-recommendations`;
    wrapper.innerHTML = slidesHTML;
    carousel.hidden = false;
    this.#renderedSlidesUrl = productUrl;

    // nw-swiper initialises (or re-creates) the carousels inside the event target.
    carousel.parentElement?.dispatchEvent(new Event('product-recommendations:loaded', { bubbles: true }));
  }

  #updateAddedProduct(cart, data, addedItem = null, fallbackImage = null) {
    const variantId = String(data.variantId || '');
    const item = cart?.items?.find((cartItem) => String(cartItem.variant_id) === variantId) || addedItem;

    if (!item) return;

    const variant = this.querySelector('[data-added-product-variant]');
    const quantity = this.querySelector('[data-added-product-quantity]');
    const price = this.querySelector('[data-added-product-price]');
    const addedQuantity = Number(data.itemCount) || 1;
    const itemImage = item.featured_image?.url || item.image || fallbackImage || '';
    const itemPrice = Number(item.final_price ?? item.price ?? 0) * addedQuantity;

    this.#setAddedProductDetails(item.product_title || item.title || '', item.url || '', itemImage);

    if (variant) {
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      variant.textContent = variantTitle;
      variant.toggleAttribute('hidden', !variantTitle);
    }

    if (quantity) quantity.textContent = String(addedQuantity);
    if (price) price.textContent = this.#formatMoney(itemPrice, cart.currency);
  }

  #updateFreeShipping(cart) {
    const freeShipping = this.querySelector('[data-cart-free-shipping]');
    if (!(freeShipping instanceof HTMLElement)) return;

    const threshold = Number(freeShipping.dataset.freeShippingThreshold) || 6000;
    const totalPrice = Number(cart.total_price) || 0;
    const remaining = Math.max(threshold - totalPrice, 0);
    const progress = Math.min(Math.round((totalPrice * 100) / threshold), 100);
    const progressBar = freeShipping.querySelector('[data-free-shipping-progress]');
    const progressValue = freeShipping.querySelector('[data-free-shipping-progress-value]');
    const message = freeShipping.querySelector('[data-free-shipping-message]');

    progressBar?.setAttribute('aria-valuenow', String(progress));
    if (progressValue instanceof HTMLElement) {
      progressValue.style.setProperty('--cart-free-shipping-progress', `${progress}%`);
    }

    if (message) {
      const template =
        remaining > 0
          ? freeShipping.dataset.freeShippingRemainingTemplate || ''
          : freeShipping.dataset.freeShippingQualifiedTemplate || '';
      message.innerHTML = template.replace('__AMOUNT__', this.#formatMoney(remaining, cart.currency));
    }
  }

  #formatMoney(value, currency) {
    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    return formatMoney(value, moneyFormat, currency || this.dataset.currency || 'EUR');
  }
}

if (!customElements.get('nw-cart-upsell-modal')) {
  customElements.define('nw-cart-upsell-modal', NwCartUpsellModal);
}
